import { NavLink } from 'react-router-dom'
import { EmptyState } from '../../../components/emptyState/emptyState'
import Spinner from '../../../components/spinner/Spinner'
import { formatCurrency } from '../../../helpers'
import { useGetTopCreatorsByChannelQuery } from '../../../redux-toolkits/reports/reports.slice'
import { DashboardReportParams } from '../../../redux-toolkits/reports/reports.type'

const formatChannelName = (key: string): string =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())

export const TopCreatorsByChannelView = ({
  mainDashQuery,
}: {
  mainDashQuery: DashboardReportParams
}) => {
  const { data, isFetching } = useGetTopCreatorsByChannelQuery(
    { ...mainDashQuery, limit: 5 },
    { refetchOnMountOrArgChange: true },
  )

  if (isFetching) {
    return <Spinner fullScreen={false} />
  }

  const channels = Object.entries(data?.data ?? {})

  if (channels.length === 0) {
    return <EmptyState text="No data available for this period." />
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {channels.map(([channelKey, creators]) => (
        <div
          key={channelKey}
          className="flex flex-col gap-0 border border-divider-gray rounded-xl overflow-hidden"
        >
          <div className="px-5 py-4 bg-gray-bg">
            <p className="font-semibold text-base">
              {formatChannelName(channelKey)}
            </p>
          </div>

          {creators.length === 0 ? (
            <EmptyState text="No creators for this channel" />
          ) : (
            <div className="flex flex-col divide-y divide-divider-gray">
              {creators.map((creator, idx) => (
                <div
                  key={creator.vendorId}
                  className="flex items-start gap-3 px-5 py-4"
                >
                  <span className="text-sec-black text-sm w-5 shrink-0 pt-0.5">
                    {idx + 1}.
                  </span>

                  <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <NavLink
                      to={`/creators/${creator.vendorId}`}
                      className="font-medium text-base hover:underline truncate"
                    >
                      {creator.businessName}
                    </NavLink>

                    <div className="flex items-center gap-2 text-sm text-sec-black flex-wrap">
                      <span>{formatCurrency(creator.totalOrderValue)}</span>
                      <span>•</span>
                      <span>{creator.totalOrders.toLocaleString()} orders</span>
                    </div>

                    {creator.topProduct && (
                      <p className="text-sm text-sec-black mt-0.5">
                        Top product:{' '}
                        <span className="text-black">
                          {creator.topProduct.name} •{' '}
                          {creator.topProduct.totalQuantity.toLocaleString()}{' '}
                          qty
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
